import { cn } from "@/lib/utils"
import {
  ChevronDownIcon,
  ExternalLink,
  HelpCircleIcon,
  Shield,
  Rocket,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { PassphraseGenerator } from "./passphrase-generator"

import { usePassphrase } from "../../lib/PassphraseProvider"
import { useSupportLinks } from "./support-links"

function SupportButton({
  dropdownClassName,
  dialogClassName,
}: {
  dropdownClassName?: string
  dialogClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [isPassphraseOpen, setIsPassphraseOpen] = useState(false)

  const { t, i18n } = useTranslation()
  const releaseLinkUrl = `https://releases.phtech.com.ua/?lang=${i18n.language}`
  const faqLink =
    "https://www.notion.so/phantom-instruction/FAQ-35d6eefd7a7a8007b5c2ef80b9cce663"
  const { setHasPassphrase, setExternalFlowActive } = usePassphrase()
  const { instructionLinkUrl, supportChatLink } = useSupportLinks()

  const openExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer")
    setOpen(false)
  }

  const handlePassphraseClick = () => {
    setOpen(false)
    setExternalFlowActive(true)
    setIsPassphraseOpen(true)
  }

  const handlePassphraseDialogChange = (newOpen: boolean) => {
    if (!newOpen) {
      // if closing, always disable external flow flag
      setExternalFlowActive(false)
    }
    setIsPassphraseOpen(newOpen)
  }

  return (
    <div className="text-foreground flex items-center space-x-4">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <div className="hover:text-primary flex cursor-pointer items-center gap-2 transition-colors">
            <span>{t("help")}</span>
            <ChevronDownIcon className="h-4 w-4" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={cn("w-48", dropdownClassName)}
        >
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2"
            onClick={() => {
              openExternalLink(faqLink)
            }}
          >
            <HelpCircleIcon className="h-4 w-4" />
            <span>{t("faq_short")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2"
            onClick={handlePassphraseClick}
          >
            <Shield className="h-4 w-4" />
            <span>{t("passphrase")}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              openExternalLink(instructionLinkUrl)
            }}
            className="flex cursor-pointer items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            <span>{t("instruction")}</span>
          </DropdownMenuItem>
          {supportChatLink && (
            <DropdownMenuItem
              onClick={() => {
                openExternalLink(supportChatLink)
              }}
              className="flex cursor-pointer items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              <span>{t("support_chat", "Support Chat")}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => {
              openExternalLink(releaseLinkUrl)
            }}
            className="flex cursor-pointer items-center gap-2"
          >
            <Rocket className="h-4 w-4" />
            <span>{t("release_notes")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={isPassphraseOpen}
        onOpenChange={handlePassphraseDialogChange}
      >
        <DialogContent className={cn("max-w-xl", dialogClassName)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t("passphrase_generator_title")}
            </DialogTitle>
          </DialogHeader>
          <PassphraseGenerator
            onPassphraseSaved={() => {
              setHasPassphrase(true)
              setExternalFlowActive(false)
            }}
            onPassphraseCleared={() => {
              setHasPassphrase(false)
              // keep external flow active; dialog remains open
            }}
            suppressGlobalEnforcement
            confirmModalClassName={dialogClassName}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SupportButton
